/**
 * Iter 188.44 · Phase 2-A · seat-nesting + type-aware qty helpers.
 *
 * The Quote Builder line-item table is a flat array of rows in state,
 * but the UI now presents them as **groups**: a base subscription
 * followed by its indented seats and a package-subtotal footer row.
 *
 * We keep the flat array as the source of truth (backend contract
 * unchanged — `line_items: List[Dict[str, Any]]`) and derive the
 * grouped rendering on the fly from three per-row fields:
 *
 *   • `_lineId`  — stable client UUID assigned when the row is added.
 *                  Used as the target of the `parent` reference below.
 *   • `parent`   — nullable `_lineId` of the base subscription this
 *                  row (a seat) attaches under.
 *   • `kind`     — 'base' | 'seat' | 'feature' | 'license' | 'program'
 *                  | 'heading' | null. Determines the qty control and
 *                  the visual treatment. Already set on all catalog
 *                  entries (Iter 333).
 *
 * All helpers here are pure and deterministic — no React, no I/O — so
 * they're trivially unit-testable and safe to call from render.
 */

/**
 * Assign a stable `_lineId` to every line item that doesn't have one.
 * Called once at load time (both for edit-existing quotes and for
 * fresh-from-catalog additions) so the parent reference chain is
 * always resolvable. Uses `crypto.randomUUID()` when available,
 * falling back to a lightweight uid for older environments.
 */
export function ensureLineIds(items) {
  if (!Array.isArray(items)) return [];
  return items.map((it) => (it && it._lineId ? it : { ...it, _lineId: uid() }));
}

function uid() {
  try {
    if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  } catch (_) { /* fallthrough */ }
  return `li-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

/**
 * All base-subscription rows in the current quote (kind === 'base'),
 * in their existing array order. Used by the "Attach to" chooser
 * when a rep adds a seat while multiple bases are present.
 */
export function getBases(items) {
  return (items || []).filter((it) => it && it.kind === "base");
}

/**
 * Return the array with `newRow` appended and, if the new row is a
 * seat, wired up to a base per the mockup rule:
 *   • Zero bases in the quote → seat is added as-is (no parent).
 *   • Exactly one base → auto-attach to it (no rep decision needed).
 *   • Multiple bases → seat arrives unattached; the row renders an
 *     amber "Attach to" chooser until the rep picks one.
 *
 * Never mutates inputs. Idempotent for non-seat rows.
 */
export function appendWithAutoAttach(items, newRow) {
  const list = Array.isArray(items) ? [...items] : [];
  const row = { ...newRow, _lineId: newRow._lineId || uid() };
  if (row.kind === "seat") {
    const bases = getBases(list);
    if (bases.length === 1) {
      row.parent = bases[0]._lineId;
    } else if (bases.length === 0) {
      row.parent = null;
    } else {
      // Multiple bases — leave `parent` undefined so the row renders
      // the amber attach-chooser. Do NOT auto-pick the first base;
      // the rep must choose (per mockup UX-02).
      row.parent = row.parent || null;
    }
  }
  list.push(row);
  return list;
}

/**
 * Compute what the render loop should paint: a flat list of "render
 * entries" tagged as either the row itself, an indent-open marker,
 * or a package-subtotal footer. The render layer walks this list
 * and doesn't have to do any grouping math.
 *
 * Every entry carries `sourceIdx` (the position in the original
 * flat array) so click handlers (delete, drag-drop, update field)
 * keep working against the source of truth without translation.
 *
 * Shape of returned entries:
 *   { type: "row",           row, sourceIdx, isChild, connector }
 *   { type: "package-subtotal", parentRow, seatCount, subtotalMonthly }
 */
export function buildRenderList(items) {
  const list = Array.isArray(items) ? items : [];
  const byId = new Map();
  list.forEach((it, i) => {
    if (it && it._lineId) byId.set(it._lineId, i);
  });

  // Pre-compute which rows are children of a valid base row (skip
  // dangling parents from stale data).
  const childrenOf = new Map();
  list.forEach((it, i) => {
    if (!it) return;
    if (it.kind !== "seat") return;
    if (!it.parent) return;
    if (!byId.has(it.parent)) return;
    const arr = childrenOf.get(it.parent) || [];
    arr.push(i);
    childrenOf.set(it.parent, arr);
  });

  const out = [];
  const consumed = new Set();

  list.forEach((it, i) => {
    if (consumed.has(i)) return;
    if (!it) {
      out.push({ type: "row", row: it, sourceIdx: i, isChild: false });
      return;
    }
    // Base row: emit the base, then all its children (attached seats)
    // inline right after — regardless of their position in the flat
    // array — plus a package-subtotal footer.
    if (it.kind === "base") {
      out.push({ type: "row", row: it, sourceIdx: i, isChild: false });
      consumed.add(i);
      const childIdxs = childrenOf.get(it._lineId) || [];
      childIdxs.forEach((cIdx, k) => {
        const child = list[cIdx];
        out.push({
          type: "row",
          row: child,
          sourceIdx: cIdx,
          isChild: true,
          connector: k === childIdxs.length - 1 ? "last" : "middle",
        });
        consumed.add(cIdx);
      });
      if (childIdxs.length > 0) {
        const subtotalMonthly = childIdxs.reduce((acc, cIdx) => {
          const c = list[cIdx];
          return acc + monthlyOnly(c);
        }, monthlyOnly(it));
        const seatCount = childIdxs.reduce((acc, cIdx) => {
          const c = list[cIdx];
          const q = parseInt(c && c.quantity, 10);
          return acc + (Number.isFinite(q) && q > 0 ? q : 1);
        }, 0);
        out.push({
          type: "package-subtotal",
          parentRow: it,
          seatCount,
          seatRowCount: childIdxs.length,
          subtotalMonthly,
        });
      }
      return;
    }
    // Non-base row that hasn't been consumed as somebody's child →
    // render at its natural position, no indent.
    out.push({ type: "row", row: it, sourceIdx: i, isChild: false });
    consumed.add(i);
  });

  return out;
}

/**
 * Only the *monthly* portion of a row's total contributes to the
 * package subtotal. Annual / one-time / rate lines are shown
 * separately in the right rail; blending them into a $/mo subtotal
 * would be misleading (spec §3).
 */
function monthlyOnly(item) {
  if (!item) return 0;
  const basis = item.basis || "fixed";
  if (basis === "annual" || basis === "onetime") return 0;
  if (basis === "pctcoll" || basis === "pctreimb") return 0;
  const t = parseFloat(item.total) || 0;
  return isFinite(t) ? t : 0;
}

/**
 * Return the array with the row at `index` removed. If the removed
 * row is a base, all of its attached seats are removed too.
 * Returns `{ next, cascadedIds }` so the caller can surface a
 * confirmation toast ("Removed EHR + 4 seats").
 */
export function removeWithCascade(items, index) {
  const list = Array.isArray(items) ? items : [];
  const target = list[index];
  if (!target) return { next: list, cascadedIds: [] };
  if (target.kind !== "base") {
    return { next: list.filter((_, i) => i !== index), cascadedIds: [] };
  }
  const cascadedIds = list
    .filter((it) => it && it.kind === "seat" && it.parent === target._lineId)
    .map((it) => it._lineId);
  const next = list.filter((it, i) => {
    if (i === index) return false;
    if (it && cascadedIds.includes(it._lineId)) return false;
    return true;
  });
  return { next, cascadedIds };
}

/**
 * Which qty control should a row show?
 *   base      → static "1", non-editable (can't add twice either)
 *   seat      → stepper (numeric with +/-)
 *   feature   → checkbox (on/off; on = qty 1)
 *   heading   → nothing
 *   volume/*  → plain numeric input (perunit, pmpm, custom, program CPT)
 */
export function qtyControlKind(item) {
  if (!item) return "numeric";
  if (item.kind === "heading") return "none";
  if (item.kind === "base") return "static";
  if (item.kind === "seat") return "stepper";
  if (item.kind === "feature") return "checkbox";
  return "numeric";
}
